// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canAddAllToList,
    canCheckoutWithCart,
    canSubmitForQuote,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import cancelApproval from "@insite/client-framework/Store/Pages/Cart/Handlers/CancelApproval";
import discardEdit from "@insite/client-framework/Store/Pages/Cart/Handlers/DiscardEdit";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import saveOrder from "@insite/client-framework/Store/Pages/Cart/Handlers/SaveOrder";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import CartSaveOrderButton from "@keller/Overrides/Widgets/Cart/CartSaveOrderButton";
import { constants } from "@keller/XDL/constants";
import { xScreen } from "@keller/XDL/Utilities";
import { createLinkIconStyles } from "@keller/XDL/Utilities/sharedStyles";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu/OverflowMenu";

const enum fields {
    fullWidthButton = "fullWidthButton",
    checkoutVariant = "checkoutVariant",
    requestForQuoteVariant = "requestForQuoteVariant",
    saveOrderVariant = "saveOrderVariant",
    addAllToListVariant = "addAllToListVariant",
}
const enum variants {
    primary = "primary",
    secondary = "secondary",
    overflow = "overflow",
    unenabled = "unenabled",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.fullWidthButton]: boolean;
        [fields.checkoutVariant]: variants;
        [fields.requestForQuoteVariant]: variants;
        [fields.saveOrderVariant]: variants;
        [fields.addAllToListVariant]: variants;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    const {
        accountSettings: { allowGuestCheckout },
        cartSettings: { onePageCheckout },
        wishListSettings: { allowMultipleWishLists },
    } = getSettingsCollection(state);
    const { isSavingOrder } = state.pages.cart;
    return {
        allowMultipleWishLists,
        cart,
        isCartEmpty: isCartEmpty(cart),
        canSaveOrder: cartState.value?.canSaveOrder,
        canAddAllToList: canAddAllToList(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled:
            isCartLoading ||
            !cart ||
            isCartCheckoutDisabled(cart) ||
            isUpdatingCart ||
            state.pages.signIn.isSigningInAsGuest,
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        isSubmitForQuoteButtonDisabled: isCartLoading || isUpdatingCart,
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        isSignedIn: cartState.value?.isAuthenticated && !cartState.value?.isGuestOrder,
        savedOrdersPageUrl: getPageLinkByPageType(state, "SavedOrderDetailsPage")?.url,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
        isSaveOrderDisabled:
            cartState.isLoading ||
            isSavingOrder ||
            isUpdatingCart ||
            cartState.value?.lineCount === 0 ||
            cartState.value?.cartLines?.length === 0,
        allowGuestCheckout,
        isApprovalEditDraft: cart?.status === "AwaitingApprovalEditDraft",
        isApprovalInProcess: cart?.orderApprovalStatus === "ApprovalInProcess",
        orderApprovalDetailsPageUrl: getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    saveOrder,
    signInAsGuest,
    discardEdit,
    cancelApproval,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;
export interface CartActionsStyles {
    narrowHidden?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuPresentationProps;
    saveOrderClickable?: ClickablePresentationProps;
    // XNG-Changes
    // addAllToListClickable?: ClickablePresentationProps;
    // requestAQuoteClickable?: ClickablePresentationProps;
    // checkoutClickable?: ClickablePresentationProps;
    wideHidden?: HiddenProps;
    wideOverflowMenu?: OverflowMenuPresentationProps;
    // XNG-Changes
    // saveOrderButton?: ButtonPresentationProps;
    // checkoutButton?: ButtonPresentationProps;
    discardEditWrapper?: InjectableCss;
    // XNG-changes
    // discardEditButton?: ButtonPresentationProps;
    discardEditLink?: LinkPresentationProps;
    cancelApprovalWrapper?: InjectableCss;
    // XNG-Changes
    // cancelApprovalButton?: ButtonPresentationProps;
    cancelApprovalButton?: LinkPresentationProps;
    // XNG-Changes
    addAllToListLink?: LinkPresentationProps;
    addAllToListClickableNarrow?: ClickablePresentationProps;
    saveOrderLink?: LinkPresentationProps;
}

export const cartActionsStyles: CartActionsStyles = {
    narrowHidden: {
        above: "sm",
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    wideHidden: {
        below: "md",
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    saveOrderLink: {
        ...createLinkIconStyles("Save"),
        css: css`
            margin-right: 85px;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                margin-right: 0;
            }
        `,
    },
    addAllToListLink: {
        ...createLinkIconStyles("List"),
    },
    wideOverflowMenu: {
        cssOverrides: {
            wrapper: css`
                margin-left: 20px;
            `,
        },
    },
    discardEditWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    // discardEditButton: {
    //     variant: "secondary",
    // },
    cancelApprovalWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    // cancelApprovalButton: {
    //     variant: "secondary",
    // },
};

const styles = cartActionsStyles;

const CartActions = ({
    cart,
    isCartEmpty,
    canAddAllToList,
    allowMultipleWishLists,
    setAddToListModalIsOpen,
    addToWishList,
    isPunchOutOrder,
    fields,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    hasOnlyQuoteRequiredProducts,
    canSubmitForQuote,
    isSubmitForQuoteButtonDisabled,
    history,
    rfqRequestQuotePageUrl,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    hasQuoteRequiredProducts,
    checkoutReviewAndSubmitPageUrl,
    checkoutShippingPageUrl,
    isSignedIn,
    saveOrder,
    savedOrdersPageUrl,
    cartPageUrl,
    signInPageUrl,
    canSaveOrder,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInAsGuest,
    rememberMe,
    isSaveOrderDisabled,
    allowGuestCheckout,
    isApprovalEditDraft,
    orderApprovalDetailsPageUrl,
    discardEdit,
    isApprovalInProcess,
    cancelApproval,
}: Props) => {
    // XNG-Changes
    const shouldHideCartActions = !cart?.cartLines?.length && !(isApprovalEditDraft || isApprovalInProcess);
    if (!cart || isPunchOutOrder || shouldHideCartActions) {
        return null;
    }

    const toasterContext = useContext(ToasterContext);
    // XNG-Changes
    // const overflowItems = [];
    // const wideHiddenOverflowMenu = [];
    // const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const [discardChangesModalIsOpen, setDiscardChangesModalIsOpen] = useState(false);

    const addAllToListClickHandler = () => {
        const productInfos =
            cart.cartLines
                ?.filter(o => o.productId && o.qtyOrdered)
                .map(cartLine => ({
                    productId: cartLine.productId!,
                    qtyOrdered: cartLine.qtyOrdered!,
                    unitOfMeasure: cartLine.unitOfMeasure,
                })) || [];
        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    // commit OOTB code
    // const checkoutClickHandler = () => {
    //     if (isPunchOutOrder) {
    //         doPunchOutCheckout();
    //         return;
    //     }
    //     if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
    //         setQuoteRequiredModalIsOpen(true);
    //         return;
    //     }
    //     const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
    //     setQuoteRequiredModalIsOpen(false);
    //     if (onePageCheckout && onePageCheckoutPageUrl) {
    //         if (!isAuthenticated && !rememberMe && allowGuestCheckout) {
    //             signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
    //         } else {
    //             history.push(
    //                 isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl
    //             );
    //         }
    //         return;
    //     }
    //     if (canBypassCheckoutAddress) {
    //         history.push(checkoutReviewAndSubmitPageUrl!);
    //         return;
    //     }
    //     preloadCheckoutShippingData({
    //         cartId: isAwaitingApproval ? cartId : undefined,
    //         onSuccess: () => {
    //             history.push(
    //                 cart?.isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cart.id}` : checkoutShippingPageUrl!,
    //             );
    //         },
    //     });
    // };

    // const requestForQuoteClickHandler = () => {
    //     if (rfqRequestQuotePageUrl) {
    //         history.push(rfqRequestQuotePageUrl);
    //     }
    // };

    // const saveOrderClickHandler = () => {
    //     if (!isSignedIn) {
    //         history.push(`${signInPageUrl}?returnUrl=${cartPageUrl}`);
    //         return;
    //     }
    //     saveOrder({
    //         onSuccess: (orderId: string) => {
    //             history.push(`${savedOrdersPageUrl}?cartId=${orderId}`);
    //         },
    //         onComplete(resultProps) {
    //             if (resultProps.apiResult?.cart) {
    //                 // "this" is targeting the object being created, not the parent SFC
    //                 // eslint-disable-next-line react/no-this-in-sfc
    //                 this.onSuccess?.(resultProps.apiResult.cart.id);
    //             }
    //         },
    //         onError: (errorMessage: string) => {
    //             toasterContext.addToast({ body: errorMessage, messageType: "danger" });
    //         },
    //     });
    // };

    // const submitForQuoteLabel = cart?.isSalesperson ? translate("Create a Quote") : translate("Submit for Quote");

    // if (canCheckoutWithCart && fields.checkoutVariant === variants.overflow) {
    //     overflowItems.push(
    //         <Clickable
    //             // {...styles.checkoutClickable}
    //             key="checkout"
    //             onClick={checkoutClickHandler}
    //             disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts}
    //         >
    //             {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout")}
    //         </Clickable>,
    //     );
    // }

    // if (canSubmitForQuote && fields.requestForQuoteVariant === variants.overflow) {
    //     overflowItems.push(
    //         <Clickable
    //             // {...styles.requestAQuoteClickable}
    //             key="requestforquote"
    //             onClick={requestForQuoteClickHandler}
    //         >
    //             {submitForQuoteLabel}
    //         </Clickable>,
    //     );
    // }

    // if (canSaveOrder && fields.saveOrderVariant === variants.overflow) {
    //     overflowItems.push(
    //         <Clickable
    //             {...styles.saveOrderClickable}
    //             key="saveorder"
    //             onClick={saveOrderClickHandler}
    //             disabled={isSaveOrderDisabled}
    //         >
    //             {translate("Save Order")}
    //         </Clickable>,
    //     );
    // }

    // if (!isCartEmpty && (!fields.addAllToListVariant || fields.addAllToListVariant === variants.overflow)) {
    //     overflowItems.push(
    //         <Clickable
    //             // {...styles.addAllToListClickable}
    //             key="addAll"
    //             disabled={!canAddAllToList}
    //             onClick={addAllToListClickHandler}
    //             data-test-selector="cartActionsAddAllToList"
    //         >
    //             {translate("Add All to List")}
    //         </Clickable>,
    //     );
    // }

    // if (overflowItems.length > 0) {
    //     wideHiddenOverflowMenu.push(
    //         <OverflowMenu key="overflow" {...styles.wideOverflowMenu} data-test-selector="cartActionsOverflowMenu">
    //             {overflowItems}
    //         </OverflowMenu>,
    //     );
    // }

    // const buttonCss = css`
    //     ${({ theme }: { theme: BaseTheme }) =>
    //         breakpointMediaQueries(theme, [
    //             css`
    //                 width: 100%;
    //             `,
    //             css`
    //                 width: 100%;
    //             `,
    //             fields.fullWidthButton
    //                 ? css`
    //                       width: 100%;
    //                   `
    //                 : null,
    //             fields.fullWidthButton
    //                 ? css`
    //                       width: 100%;
    //                   `
    //                 : null,
    //             fields.fullWidthButton
    //                 ? css`
    //                       width: 100%;
    //                   `
    //                 : null,
    //         ])}
    // `;

    if (isApprovalEditDraft) {
        const discardEditClickHandler = async () => {
            discardEdit({
                cart,
                onSuccess: () => {
                    if (orderApprovalDetailsPageUrl) {
                        history.push(`${orderApprovalDetailsPageUrl}?cartId=${cart.orderNumber.split("_")[1]}`);
                    }
                },
            });
        };

        return (
            <StyledWrapper {...styles.discardEditWrapper}>
                {/* XNG-Changes - Replace Button with Link */}
                {/* <Button {...styles.discardEditButton} onClick={() => setDiscardChangesModalIsOpen(true)}>
                    {translate("Discard edit")}
                </Button> */}
                <Link {...styles.discardEditLink} onClick={() => setDiscardChangesModalIsOpen(true)}>
                    {translate("Discard edit")}
                </Link>
                <TwoButtonModal
                    headlineText={translate("Are you sure?")}
                    modalIsOpen={discardChangesModalIsOpen}
                    messageText={siteMessage("OrderApproval_DiscardEditConfirmationMessage")}
                    cancelButtonText={translate("Continue editing")}
                    submitButtonText={translate("Discard edit")}
                    onCancel={() => setDiscardChangesModalIsOpen(false)}
                    onSubmit={discardEditClickHandler}
                ></TwoButtonModal>
            </StyledWrapper>
        );
    }

    if (isApprovalInProcess) {
        const cancelApprovalClickHandler = async () => {
            cancelApproval({
                cart,
                onSuccess: () => {
                    if (orderApprovalDetailsPageUrl) {
                        history.push(`${orderApprovalDetailsPageUrl}?cartId=${cart.id}`);
                    }
                },
            });
        };

        return (
            <StyledWrapper {...styles.cancelApprovalWrapper}>
                {/* XNG-Changes - Replace Button with Link */}
                <Link {...styles.cancelApprovalButton} onClick={cancelApprovalClickHandler}>
                    {translate("Save and continue later")}
                </Link>
            </StyledWrapper>
        );
    }

    return (
        <>
            <CartSaveOrderButton variant="link" extendedStyles={styles.saveOrderLink} />
            <Link
                {...styles.addAllToListLink}
                key="addAll"
                disabled={!canAddAllToList}
                onClick={addAllToListClickHandler}
                data-test-selector="cartActionsAddAllToList"
            >
                {translate("Add All to List")}
            </Link>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartActions)),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
    },
};

export default widgetModule;
